import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BsChevronLeft } from "react-icons/bs";
import "./DetailPageHeader.css";

export interface DetailPageHeaderProps {
  onBack: () => void;
  /** Uppercase context label shown above the title (e.g. "Events"). */
  eyebrow: string;
  title: string;
  /** Optional meta row under the title (icon + text). */
  meta?: ReactNode;
  /** Optional trailing actions (e.g. download / search) — right on md+. */
  actions?: ReactNode;
  /** Extra class for the actions wrapper (e.g. desktop-only toolbar). */
  actionsClassName?: string;
  className?: string;
}

function DetailPageHeader({
  onBack,
  eyebrow,
  title,
  meta,
  actions,
  actionsClassName = "",
  className = "",
}: DetailPageHeaderProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`detail-page-header card shadow border-0 overflow-hidden mb-3 ${className}`.trim()}
    >
      <div className="detail-page-header__inner position-relative overflow-hidden border-0 bg-project-gradient text-white">
        <button
          type="button"
          className="detail-page-header__back position-relative z-1 d-inline-flex align-items-center fw-semibold text-white border rounded mb-3"
          onClick={onBack}
          aria-label={t("auth.back")}
          title={t("auth.back")}
        >
          <BsChevronLeft size={14} aria-hidden />
          <span>{t("auth.back")}</span>
        </button>

        <div className="detail-page-header__body position-relative z-1 d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between gap-3 min-w-0">
          <div className="detail-page-header__copy flex-grow-1 min-w-0 pe-md-2">
            <div className="detail-page-header__eyebrow text-uppercase fw-semibold mb-1">
              {eyebrow}
            </div>
            <h1 className="detail-page-header__title text-white fw-bold mb-0">
              {title}
            </h1>
            {meta ? (
              <div className="detail-page-header__meta d-inline-flex align-items-center gap-2 fw-semibold mt-2">
                {meta}
              </div>
            ) : null}
          </div>
          {actions ? (
            <div
              className={`detail-page-header__actions flex-shrink-0 ${actionsClassName}`.trim()}
            >
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default DetailPageHeader;
